import React, { FC, ReactElement, useCallback, useState } from 'react';

import Button, { BUTTON_WIDTHS } from 'buyside/components/presentation/Button/button';
import FilterCheckbox from 'buyside/components/presentation/Filters/FilterCheckbox/filterCheckbox';
import FilterChecklist from 'buyside/components/presentation/Filters/FilterChecklist/filterChecklist';
import FilterSelect from 'buyside/components/presentation/Filters/FilterSelect/filterSelect';
import FilterSliderSingle from 'buyside/components/presentation/Filters/FilterSliderSingle/filterSliderSingle';
import Modal from 'buyside/components/presentation/Modal/modal';
import FilterTextInput from 'buyside/components/presentation/Filters/FilterTextInput/filterTextInput';
import FundProfileSavedSearchContainer from 'buyside/components/container/FundProfileIndex/FundProfileSavedSearchContainer';
import SelectableDropdown from 'buyside/components/presentation/SelectableDropdown/selectableDropdown';

import { labelizedFilterListOptions } from 'buyside/components/presentation/Filters/filterUtil';
import { sortByKey } from 'buyside/util/lodashAlt';
import {
  timeframes,
  visibleResearchOptions,
  managementFee,
  incentiveFee,
  yearsOperating,
  sortBy
} from 'buyside/util/fundProfileFilterConstants';

const {
  assetClasses,
  aumRanges,
  cities,
  fundFocuses,
  fundStrategies,
  fundSupportingDocuments,
  fundTags,
  fundTypes,
  geographies,
  quartiles,
  sectors,
  yesNo
} = labelizedFilterListOptions;

import styles from './fundProfileIndexPageStyles.scss';

export const renderFundCheckboxes = (showAllocatorNotification: boolean): ReactElement => (
  <>
    {showAllocatorNotification && (
      <FilterCheckbox text="Show Interested Funds" paramName="intro_request" />
    )}
    <FilterCheckbox text="Has Supplied References" paramName="has_supplied_references" />
    <FilterCheckbox text="Has Listed Fund Positions" paramName="has_listed_holdings" />
    <FilterCheckbox
      text="Has Completed Due Diligence Section"
      paramName="has_completed_due_diligence"
    />
  </>
);

export const renderFundSortOrders = (): ReactElement => (
  <FilterSelect paramName="sort_funds_by" options={sortBy} borderless />
);

const FundProfileFilters: FC = () => {
  const [isFirmCharacteristicsModalOpen, setIsFirmCharacteristicsModalOpen] = useState(false);
  const [isKnowledgeAreasModalOpen, setIsKnowledgeAreasModalOpen] = useState(false);

  const renderOpenFirmCharacteristicsModalButton = useCallback(
    () => (
      <div>
        <Button
          className={styles.filterGroupModalButton}
          onClick={() => setIsFirmCharacteristicsModalOpen(true)}
          text="Firm Characteristics"
          width={BUTTON_WIDTHS.FULL}
        />
      </div>
    ),
    []
  );

  const renderOpenKnowledgeAreasModalButton = useCallback(
    () => (
      <div>
        <Button
          className={styles.filterGroupModalButton}
          onClick={() => setIsKnowledgeAreasModalOpen(true)}
          text="Knowledge Areas"
          width={BUTTON_WIDTHS.FULL}
        />
      </div>
    ),
    []
  );

  return (
    <>
      <SelectableDropdown title="Saved Searches">
        <FundProfileSavedSearchContainer />
      </SelectableDropdown>
      <FilterTextInput
        placeholder="Keyword"
        paramName="text"
        id="keyword-search"
        disabled={false}
      />
      <SelectableDropdown title="AUM">
        <FilterChecklist filterList={aumRanges} paramName="aum_range" />
      </SelectableDropdown>
      <SelectableDropdown title="Years Operating" overflowVisible>
        <FilterChecklist filterList={yearsOperating} paramName="years_operating" />
      </SelectableDropdown>
      <SelectableDropdown title="Firm Type">
        <FilterChecklist filterList={sortByKey(fundTypes, 'value')} paramName="fund_type" />
      </SelectableDropdown>
      <SelectableDropdown title="Strategy">
        <FilterChecklist
          filterList={fundStrategies.sort((a, b) => (a.value > b.value ? 1 : -1))}
          paramName="fund_strategy"
        />
      </SelectableDropdown>
      <SelectableDropdown title="Focus">
        <FilterChecklist filterList={fundFocuses} paramName="fund_focus" />
      </SelectableDropdown>
      <SelectableDropdown title="Returns">
        <div className={styles.returnInstructions}>
          Please select both fields to initiate search:
        </div>
        <FilterSelect text="Timeframe" paramName="return_timeframe" options={timeframes} />
        <FilterSelect text="Quartile" paramName="return_quartile" options={quartiles} />
      </SelectableDropdown>
      <Modal
        isOpen={isKnowledgeAreasModalOpen}
        onClose={() => setIsKnowledgeAreasModalOpen(false)}
        renderOpenModalButton={renderOpenKnowledgeAreasModalButton}
        size="lg"
        title="Knowledge Areas"
      >
        <div className={styles.filterGroupGrid}>
          <FilterChecklist title="Sectors" paramName="fund_sectors[]" filterList={sectors} />
          <FilterChecklist title="Geographies" paramName="geographies[]" filterList={geographies} />
          <FilterChecklist
            title="Asset Classes"
            paramName="asset_classes[]"
            filterList={assetClasses}
          />
        </div>
      </Modal>
      <Modal
        isOpen={isFirmCharacteristicsModalOpen}
        onClose={() => setIsFirmCharacteristicsModalOpen(false)}
        renderOpenModalButton={renderOpenFirmCharacteristicsModalButton}
        size="lg"
        title="Firm Characteristics"
      >
        <div className={styles.filterGroupGrid}>
          {fundTags.map(
            ({
              groupName,
              tags
            }: {
              groupName: string;
              tags: { label: string; value: string }[];
            }) => (
              <FilterChecklist
                key={`${groupName}_fund_tags`}
                filterList={tags}
                paramName="fund_tags[]"
                title={groupName}
              />
            )
          )}
        </div>
      </Modal>
      <SelectableDropdown title="Research">
        <FilterChecklist filterList={visibleResearchOptions} paramName="research_available" />
      </SelectableDropdown>
      <SelectableDropdown title="Supporting Documents">
        <FilterChecklist filterList={fundSupportingDocuments} paramName="supporting_documents[]" />
      </SelectableDropdown>
      <SelectableDropdown title="Key Metrics" overflowVisible>
        <FilterSelect text="Beta" paramName="beta_metric" options={quartiles} />
        <FilterSelect text="Sharpe Ratio" paramName="sharpe_metric" options={quartiles} />
        <FilterSelect text="Turnover Ratio" paramName="turnover_metric" options={quartiles} />
      </SelectableDropdown>
      <SelectableDropdown title="Fee Structure" overflowVisible>
        <FilterSelect text="Management Fee" paramName="management_fee" options={managementFee} />
        <FilterSelect text="Incentive Fee" paramName="incentive_fee" options={incentiveFee} />
        <FilterSelect text="Hurdle Rate" paramName="hurdle_rate" options={yesNo} />
        <FilterSelect text="Lock Up" paramName="lock_up" options={yesNo} />
        <FilterSelect text="Highwater Mark" paramName="highwater_mark" options={yesNo} />
        <FilterSelect text="Seed Deals" paramName="seed_deal" options={yesNo} />
        <FilterSelect text="Founder's Shares" paramName="founders_shares" options={yesNo} />
        <FilterSelect
          text="Investor-Level Gates"
          paramName="investor_level_gates"
          options={yesNo}
        />
      </SelectableDropdown>
      <SelectableDropdown title="Location" overflowVisible>
        <FilterSelect paramName="city" options={cities} />
      </SelectableDropdown>
      <SelectableDropdown title="Profile Completeness">
        <FilterSliderSingle
          paramName="completeness"
          ticks={[0, 20, 40, 60, 80, 100]}
          text="Min. Completeness"
          interval={1}
        />
      </SelectableDropdown>
    </>
  );
};

export default FundProfileFilters;
